import type { HaftkhabisEvent, HaftkhabisPhase } from "./types";

/*
 * THE TABLE'S CLOCK, ONE TABLE OF NUMBERS FOR THREE READERS: the web draws every moment to it
 * (`apps/web/src/game/haftkhabis-moments.ts`), `postMoveDelayMs` holds a bot for it, and the game-server's
 * scripted beat holds a human's first window of a round for it. So a bot cannot outrun what the screen is
 * still showing, and the opening cannot eat a window: there is one number for each span, and it lives here.
 * No clock is read: these are durations, and the functions read events a state already holds.
 */

/** A played card's flight, from its seat onto the discard. */
export const PLAY_FLIGHT_MS = 360;
/** A card off the pile to a seat: a deal's, a draw's, a penalty's. Longer than `card-deal.mp3`'s 296 ms lead. */
export const DRAW_FLIGHT_MS = 340;
/** A blind take's card, between the two seats. */
export const TAKE_FLIGHT_MS = 360;

// ── A round's opening: the deal, the fan, the first card turned up, and only then the turn ──
/** Between two cards leaving the pile, one at a time round the table. */
export const DEAL_STEP_MS = 90;
/** Your seven spreading from their stack into the fan. */
export const FAN_MS = 280;
/** The pile's top card turned face up onto the discard. */
export const FLIP_MS = 420;
/** D1: a special turned up lies there this long… */
export const TURNED_BACK_SHOW_MS = 360;
/** …and goes back to the pile. */
export const TURNED_BACK_RETURN_MS = 260;
/** The most D1 specials the table shows one by one (the engine's limit is far higher, and never met in play). */
export const MAX_TURNED_BACK_SHOWN = 3;
/** The first card is up: this much later the first player's turn shows. */
export const OPENING_REST_MS = 160;

/** A deal's cards: seven to each of four seats. */
const DEALT_CARDS = 28;

/** The last card's landing, from the deal's start. */
export const DEAL_LANDED_MS = (DEALT_CARDS - 1) * DEAL_STEP_MS + DRAW_FLIGHT_MS;

/** A round's opening on screen, from the first card leaving the pile to the turn showing. */
export function openingSpanMs(turnedBack: number): number {
  const shown = Math.min(Math.max(0, turnedBack), MAX_TURNED_BACK_SHOWN);
  return (
    DEAL_LANDED_MS +
    FAN_MS +
    shown * (FLIP_MS + TURNED_BACK_SHOW_MS + TURNED_BACK_RETURN_MS) +
    FLIP_MS +
    OPENING_REST_MS
  );
}

// ── A round's end: the winning card lands, a beat, every remaining hand, the table of points ──
/** After the winning card has landed, before the hands. */
export const ROUND_BEAT_MS = 450;
/** The remaining hands alone on the round's card. */
export const ROUND_HANDS_MS = 1500;
/** The table of points. */
export const ROUND_POINTS_MS = 2800;
/** A round's end on screen, from the winning card leaving its seat to the table of points going. */
export const ROUND_END_SPAN_MS = PLAY_FLIGHT_MS + ROUND_BEAT_MS + ROUND_HANDS_MS + ROUND_POINTS_MS;

// ── One move's effect, after its card has landed ──
/** The card has landed: this much later its effect shows («a play lands, THEN its effect»). */
export const EFFECT_GAP_MS = 80;
/** How long each effect holds the table before the turn moves on. */
export const SEVEN_BEAT_MS = 420;
export const BONUS_BEAT_MS = 420;
/** …the skipped seat carrying the turn's glow for it, so the turn visibly passes over that seat. */
export const SKIP_BEAT_MS = 520;
/** The direction mark's one flip. */
export const REVERSE_BEAT_MS = 760;
export const SUIT_BEAT_MS = 480;
/** A 2's target named, before the take (or the draw) follows. */
export const TARGET_BEAT_MS = 520;
/** A press carried on a play that has an effect too: the press's bubble first. */
export const PRESS_BEAT_MS = 400;
/** A press on its own: its bubble is up this soon, the moment ends with it. */
export const PRESS_MS = 60;
/** A penalty paid: between two cards leaving the pile, one at a time. */
export const PAY_STEP_MS = 180;
/** A catch: the bubble, then the penalty card leaves the pile this much later. */
export const CATCH_BEAT_MS = 420;
/** D3: the discards become the new pile, before the card that needed it. */
export const RESHUFFLE_MS = 700;
/** A draw that gave nothing (D3): its line on screen before the table moves on. */
export const NOTHING_TO_DRAW_MS = 500;

/** What a bot waits for when no span asks for more: the rooms' flat cadence. */
const FLAT_CADENCE_MS = 700;
/** A move's span has ended: the next bot's card leaves this much later at the soonest. */
export const MOVE_REST_MS = 150;

type Body = HaftkhabisEvent;

/** Is this event where a move begins? (`reshuffle` comes first when it comes, and belongs to what follows.) */
function isHead(event: Body, before: Body | undefined): boolean {
  switch (event.kind) {
    case "play":
    case "take":
    case "pass":
    case "penaltyPaid":
    case "catch":
      return true;
    case "draw":
      return event.reason !== "two"; // a 2's target draws inside the 2's own move
    case "press":
      // carried on that play, or on the draw an 8-last owes (D14), or on its own
      return !((before?.kind === "play" || before?.kind === "draw") && before.seat === event.seat);
    default:
      return false;
  }
}

/** The events of the last move in a tail: from its head (and the reshuffle before it) to the end. */
export function lastMoveEvents(events: readonly Body[]): readonly Body[] {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]!;
    if (event.kind === "roundStart") return [];
    if (isHead(event, events[i - 1])) {
      return events.slice(i > 0 && events[i - 1]!.kind === "reshuffle" ? i - 1 : i);
    }
  }
  return [];
}

/** When a played card's effect shows, from the move's start: after its landing, and after a press it carried. */
export function playEffectAtMs(move: readonly Body[]): number {
  const pressed = move.some((e) => e.kind === "press");
  return PLAY_FLIGHT_MS + (pressed ? PRESS_BEAT_MS : EFFECT_GAP_MS);
}

/** One move on screen, from its start to the turn moving on. A round's end has its own span. */
export function moveSpanMs(move: readonly Body[]): number {
  const reshuffle = move.some((e) => e.kind === "reshuffle") ? RESHUFFLE_MS : 0;
  const head = move.find((e) => e.kind !== "reshuffle");
  if (head === undefined) return 0;
  switch (head.kind) {
    case "play": {
      const effectAt = playEffectAtMs(move);
      for (const e of move) {
        switch (e.kind) {
          case "seven":
            return effectAt + SEVEN_BEAT_MS;
          case "bonus":
            return effectAt + BONUS_BEAT_MS;
          case "skip":
            return effectAt + SKIP_BEAT_MS;
          case "reverse":
            return effectAt + REVERSE_BEAT_MS;
          case "suit":
            return effectAt + SUIT_BEAT_MS;
          case "target": {
            if (e.via === "take") return effectAt + TARGET_BEAT_MS;
            const drew = move.find((d) => d.kind === "draw");
            const count = drew?.kind === "draw" ? drew.count : 0;
            return (
              effectAt +
              TARGET_BEAT_MS +
              reshuffle +
              (count > 0 ? DRAW_FLIGHT_MS : NOTHING_TO_DRAW_MS)
            );
          }
          default:
        }
      }
      return PLAY_FLIGHT_MS;
    }
    case "draw":
      return reshuffle + (head.count > 0 ? DRAW_FLIGHT_MS : NOTHING_TO_DRAW_MS);
    case "penaltyPaid":
      return (
        reshuffle +
        (head.count > 0 ? (head.count - 1) * PAY_STEP_MS + DRAW_FLIGHT_MS : NOTHING_TO_DRAW_MS)
      );
    case "take":
      return TAKE_FLIGHT_MS;
    case "press":
      return PRESS_MS;
    case "catch":
      return PRESS_MS + (head.count > 0 ? CATCH_BEAT_MS + reshuffle + DRAW_FLIGHT_MS : 0);
    default:
      return 0;
  }
}

/** How long the state's last move holds the next bot: its span and a rest, or nothing past the flat cadence. */
export function moveHoldMs(events: readonly Body[]): number | undefined {
  const hold = moveSpanMs(lastMoveEvents(events)) + MOVE_REST_MS;
  return hold > FLAT_CADENCE_MS ? hold : undefined;
}

/**
 * How long the screen holds the hand of whoever acts next: the last move's span. A side move (a press, a catch) moves no
 * turn and holds no hand, and a round's first move has nothing before it (`lastMoveEvents` stops at `roundStart`): the
 * game-server gives this time back to the window that opens under it (`trick-end-hold.ts`, the operator's rule of 2026-10-11).
 */
export function handHoldMs(events: readonly Body[]): number {
  const move = lastMoveEvents(events);
  const head = move.find((e) => e.kind !== "reshuffle");
  return head?.kind === "press" || head?.kind === "catch" ? 0 : moveSpanMs(move);
}

/** How many D1 specials this round's opening showed (the tail's last `roundStart`). */
export function turnedBackOf(events: readonly Body[]): number {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]!;
    if (event.kind === "roundStart") return event.turnedBack.length;
  }
  return 0;
}

/**
 * The round's opening on screen: the state (or any seat's view) whose first action must wait for the
 * deal (and, after round 0, the round's end first): `null` once a move was made, and at the match's end.
 * `postMoveDelayMs` holds a bot for exactly this; the game-server's scripted beat asks this too
 * (`scriptedBeat: (view) => haftkhabisOpeningBeatMs(view) !== null`), holding a human's window, and an
 * expiry after a beat is never an idle miss.
 */
export function haftkhabisOpeningBeatMs(facts: {
  readonly phase: HaftkhabisPhase;
  readonly round: number;
  readonly moveCount: number;
  readonly events: readonly Body[];
}): number | null {
  if (facts.phase === "gameOver" || facts.moveCount !== 0) return null;
  const opening = openingSpanMs(turnedBackOf(facts.events));
  return facts.round === 0 ? opening : ROUND_END_SPAN_MS + opening;
}
